import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { ValueChartModal, ValueChartModalRef } from '@/feature/educational-picks/components/ValueChartModal';
import { designSystem, withOpacity } from '@/styles/styles';
import { MODAL_PADDINGS, isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

import { PickTypeEducationItem, usePickTypesEducationData } from '../hooks/use-pick-types-education-data';
import {
    PickTypesEducationTarget,
    getPickTypeEducationKey,
    pickTypeMatchesEducationKey,
} from '../utils/pick-types-education-target';

export type PickTypesEducationModalRef = {
    show: (target?: PickTypesEducationTarget) => void;
};
const MODAL_HEIGHT = 534;

// fixed height on mobile; let the sheet size dynamically on web
const SNAP_POINTS = isWeb ? undefined : [MODAL_HEIGHT];

const modalId = 'pickTypesEducation';

const WEB_MAX_CONTENT_WIDTH = 680;
const WEB_DETAILS_MIN_HEIGHT = 350;

export const PickTypesEducationModal = React.forwardRef<PickTypesEducationModalRef, {}>((_props, ref) => {
    const { width } = useWindowDimensions();
    const webContentStyle = isWeb ? { width: Math.min(WEB_MAX_CONTENT_WIDTH, width - MODAL_PADDINGS) } : undefined;
    const modalRef = useRef<BottomSheetModal>(null);
    const valueChartRef = useRef<ValueChartModalRef>(null);
    const [initialPickTypeKey, setInitialPickTypeKey] = useState<string | undefined>(undefined);
    useImperativeHandle(ref, () => ({
        show: target => {
            setInitialPickTypeKey(target ? getPickTypeEducationKey(target) : undefined);
            modalRef.current?.present();
        },
    }));

    const onClose = () => {
        modalRef.current?.dismiss();
    };

    const onOpenValueChart = useCallback(() => {
        valueChartRef.current?.show();
    }, []);

    return (
        <>
            <Modal
                id={modalId}
                modalRef={modalRef}
                backgroundColor={ds.colorValue.background.neutral.base}
                snapPoints={SNAP_POINTS}
                hideCloseButtonOnWeb
                enableDynamicSizing={isWeb}
            >
                <Box style={webContentStyle}>
                    <Row alignItems={'center'} paddingHorizontal={'s8'} marginTop={'s4'} marginBottom={'s12'}>
                        <TouchableBox onPress={onClose} padding={'s12'} hitSlop={20}>
                            <CloseSmallIcon />
                        </TouchableBox>
                        <Text
                            variant={'titleMedium2'}
                            color={ds.colorToken.text.primary}
                            textAlign={'center'}
                            style={styles.title}
                        >
                            Pick Types
                        </Text>
                        {/* spacer to keep the title centered against the close icon */}
                        <Box width={44} />
                    </Row>
                    <ModalContent initialPickTypeKey={initialPickTypeKey} onOpenValueChart={onOpenValueChart} />
                </Box>
            </Modal>
            <ValueChartModal ref={valueChartRef} />
        </>
    );
});

const ModalContent = ({
    initialPickTypeKey,
    onOpenValueChart,
}: {
    initialPickTypeKey?: string;
    onOpenValueChart: () => void;
}) => {
    const { data, isLoading } = usePickTypesEducationData();
    const pickTypes = useMemo(() => data?.pickTypes ?? [], [data?.pickTypes]);
    const listRef = useRef<FlatList<PickTypeEducationItem>>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback((index: number, animated: boolean = true) => {
        setSelectedIndex(index);
        listRef.current?.scrollToIndex({
            index,
            animated,
            viewPosition: 0.5, // bring the tapped tab toward the center
            viewOffset: 0,
        });
    }, []);

    // index of the pick type whose `projection_types` (from Prismic) contains the tapped pick type key
    const initialIndex = useMemo(() => {
        if (!initialPickTypeKey) {
            return 0;
        }
        const index = pickTypes.findIndex(item =>
            pickTypeMatchesEducationKey(item.projection_types, initialPickTypeKey)
        );
        return index === -1 ? 0 : index;
    }, [pickTypes, initialPickTypeKey]);

    useEffect(() => {
        setSelectedIndex(initialIndex);
        if (initialIndex === 0) {
            return;
        }
        const timeout = setTimeout(() => onSelect(initialIndex, false), 100);
        return () => clearTimeout(timeout);
    }, [initialIndex, onSelect]);

    if (isLoading) {
        return (
            <Box paddingVertical={'s40'} alignItems={'center'} justifyContent={'center'}>
                <Loading />
            </Box>
        );
    }

    const selectedItem = pickTypes[selectedIndex];

    return (
        <Box>
            <FlatList<PickTypeEducationItem>
                ref={listRef}
                data={pickTypes}
                keyExtractor={(item, index) => `${index}-${item.pick_type_name}`}
                renderItem={({ item, index }) => (
                    <PickTypeTab item={item} isSelected={index === selectedIndex} onPress={() => onSelect(index)} />
                )}
                onScrollToIndexFailed={error => {
                    // if the scrollToIndex fails (item not yet measured), scroll to the estimated offset
                    listRef.current?.scrollToOffset({
                        offset: error.averageItemLength * error.index,
                        animated: true,
                    });
                }}
                horizontal
                contentContainerStyle={styles.listContent}
                showsHorizontalScrollIndicator={showScrollIndicator}
            />
            {selectedItem ? <PickTypeDetails item={selectedItem} onOpenValueChart={onOpenValueChart} /> : null}
        </Box>
    );
};

const PickTypeTab = ({
    item,
    isSelected,
    onPress,
}: {
    item: PickTypeEducationItem;
    isSelected: boolean;
    onPress: () => void;
}) => {
    const iconUrl = isSelected ? item.icon_selected?.url : item.icon?.url;
    const selectedColor = item.pick_type_color || designSystem.colors.neutral300;
    const badgeStyle = { backgroundColor: isSelected ? selectedColor : designSystem.colors.neutral300 };

    return (
        <TouchableBox onPress={onPress}>
            {isSelected ? <Box style={styles.border} /> : null}
            <Box
                flexDirection={'row'}
                alignItems={'center'}
                gap={'s8'}
                paddingHorizontal={'s14'}
                paddingVertical={'s10'}
                style={isSelected ? styles.tabSelected : undefined}
            >
                <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    padding={'s2'}
                    borderRadius={'r6'}
                    style={badgeStyle}
                >
                    {iconUrl ? <Image source={{ uri: iconUrl }} style={styles.tabIcon} resizeMode={'contain'} /> : null}
                </Box>
                <Text variant={'labelLarge2'} color={isSelected ? ds.colorToken.text.primary : 'neutral300'}>
                    {item.pick_type_name}
                </Text>
            </Box>
        </TouchableBox>
    );
};

const PickTypeDetails = ({ item, onOpenValueChart }: { item: PickTypeEducationItem; onOpenValueChart: () => void }) => {
    const icon3dUrl = item.icon_3d?.url;

    return (
        <Box
            alignItems={'center'}
            paddingHorizontal={'s24'}
            style={[styles.container, isWeb ? styles.webStyle : undefined]}
        >
            {icon3dUrl ? <Image source={{ uri: icon3dUrl }} style={styles.icon3d} resizeMode={'contain'} /> : null}
            <Text variant={'headlineMedium'} color={ds.colorToken.text.primary} textAlign={'center'} marginTop={'s16'}>
                {item.pick_type_name}
            </Text>
            {item.pick_type_subtitle ? (
                <Box
                    marginTop={'s8'}
                    style={{
                        backgroundColor: withOpacity(item.pick_type_color || designSystem.colors.neutral200, 0.1),
                    }}
                    paddingVertical={'s2'}
                    paddingHorizontal={'s6'}
                    borderRadius={'r6'}
                >
                    <Text
                        variant={'labelMedium'}
                        textAlign={'center'}
                        style={{ color: item.pick_type_color || ds.colorValue.text.secondary }}
                    >
                        {item.pick_type_subtitle}
                    </Text>
                </Box>
            ) : null}
            <Text variant={'bodyMedium'} color={ds.colorToken.text.secondary} textAlign={'center'} marginTop={'s8'}>
                {item.pick_type_description}
            </Text>
            {item.value_chart ? (
                <TouchableBox
                    onPress={onOpenValueChart}
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    paddingHorizontal={'s10'}
                    paddingVertical={'s4'}
                    marginTop={'s16'}
                    style={styles.valueChartButton}
                >
                    <Text variant={'bodySmaller'} color={ds.colorToken.text.primary} fontWeight={'600'}>
                        Value chart
                    </Text>
                </TouchableBox>
            ) : null}
        </Box>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingTop: 60,
    },
    title: {
        flex: 1,
    },
    webStyle: {
        minHeight: WEB_DETAILS_MIN_HEIGHT,
    },
    listContent: {
        paddingHorizontal: 16,
    },
    tabSelected: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderRadius: 20,
    },
    border: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: 20,
        zIndex: 1,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
    },
    tabIcon: {
        width: 16,
        height: 16,
    },
    icon3d: {
        width: 104,
        height: 104,
    },
    valueChartButton: {
        borderRadius: 28,
    },
});
